import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export default function About() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative h-[400px] md:h-[560px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/0855e226-c143-4f1f-b5c3-022f0d921b96_compressed_Grupo-1.webp"
            alt="San Lorenzo School"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-purple-900/60 via-purple-900/50 to-purple-900/70" />
        </div>
        <div className="relative z-10 text-center px-4">
          <span className="text-yellow-400 text-xs font-semibold tracking-[0.2em] uppercase">{t('about.heroBadge')}</span>
          <h1 className="font-serif text-4xl md:text-6xl font-bold text-white mt-4 leading-tight">{t('about.title')}</h1>
        </div>
      </section>

      {/* History */}
      <section id="history" data-animate className="py-20 md:py-28 bg-white">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[55%_40%] gap-12 md:gap-20 items-center">
            <div className={`transition-all duration-700 ${isVisible['history'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('about.historyBadge')}</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mt-4 mb-6 leading-tight">{t('about.history.title')}</h2>
              <p className="text-black/50 leading-relaxed text-base">{t('about.history.desc')}</p>
            </div>
            <div className={`rounded-2xl overflow-hidden flex items-center justify-center transition-all duration-700 delay-200 ${isVisible['history'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <img
                src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/4dd901b5-cc9a-4a4f-bf28-a2c23c473e64_compressed_MagnanimidadF_comprimida.webp"
                alt={t('about.history.title')}
                className="w-full h-64 md:h-auto md:max-h-[460px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section id="mission" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className={`bg-white rounded-2xl p-8 md:p-10 border border-purple-100 transition-all duration-700 ${isVisible['mission'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="w-14 h-14 rounded-xl bg-teal-500 flex items-center justify-center mb-6">
                <i className="ri-compass-line text-yellow-400 text-2xl" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-purple-900 mb-4">{t('about.mission.title')}</h2>
              <p className="text-black/50 leading-relaxed text-base">{t('about.mission.desc')}</p>
            </div>
            <div className={`bg-white rounded-2xl p-8 md:p-10 border border-purple-100 transition-all duration-700 delay-150 ${isVisible['mission'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <div className="w-14 h-14 rounded-xl bg-teal-500 flex items-center justify-center mb-6">
                <i className="ri-eye-line text-yellow-400 text-2xl" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-purple-900 mb-4">{t('about.vision.title')}</h2>
              <p className="text-black/50 leading-relaxed text-base">{t('about.vision.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section id="philosophy" data-animate className="py-20 md:py-28 bg-purple-50">
        <div className="w-full px-4 md:px-8 lg:px-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className={`order-2 lg:order-1 rounded-2xl overflow-hidden transition-all duration-700 ${isVisible['philosophy'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <img
                src="https://storage.readdy-site.link/project_files/2dbf3bce-c893-400a-8ef5-86be21e9463b/89049892-12f8-47e4-bff4-3d38cc25805f_compressed_Pilar2.webp"
                alt={t('about.philosophy.title')}
                className="w-full h-64 md:h-full object-cover"
              />
            </div>
            <div className={`order-1 lg:order-2 transition-all duration-700 delay-150 ${isVisible['philosophy'] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
              <span className="text-yellow-500 text-xs font-semibold tracking-[0.2em] uppercase">{t('about.philosophyBadge')}</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-purple-900 mt-4 mb-6 leading-tight">{t('about.philosophy.title')}</h2>
              <ul className="space-y-3">
                {(t('about.philosophy.desc', { returnObjects: true }) as string[]).map((item: string, idx: number) => (
                  <li key={idx} className="flex items-start gap-3 text-black/50 text-base">
                    <span className="text-teal-500 mt-0.5 flex-shrink-0">&bull;</span>
                    <span className="leading-relaxed">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}